import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import {
  createTelephonyPoolMutation,
  listTelephonyPoolsQueryKey,
  marketLabel,
  marketLabels,
  ownerTypeLabel,
  poolStatus,
  type TelephonyPoolView,
  useTelephonyPools,
} from '@/entities/telephony'
import { errorMessage } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { CardLink } from '@/shared/ui/card'
import { Chip } from '@/shared/ui/chip'
import { EmptyState } from '@/shared/ui/empty-state'
import { SelectField, TextField } from '@/shared/ui/field'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { SlideOver } from '@/shared/ui/slide-over'
import { Spinner } from '@/shared/ui/spinner'
import { StatusBadge } from '@/shared/ui/status-badge'

export const Route = createFileRoute('/telephony/pools/')({
  component: PoolsPage,
})

function PoolsPage() {
  const { pools, isPending, isError, error, refetch } = useTelephonyPools()
  const [createOpen, setCreateOpen] = useState(false)

  return (
    <>
      <PageHeader
        title="Пулы"
        description="Группы линий, между которыми роутятся звонки"
        actions={
          <Button variant="accent" onClick={() => setCreateOpen(true)}>
            <Icon name="plus" size={18} />
            Завести пул
          </Button>
        }
      />

      {isPending ? (
        <div className="flex justify-center py-24 text-muted">
          <Spinner size={28} />
        </div>
      ) : isError ? (
        <EmptyState
          icon="alert"
          title="Не удалось загрузить пулы"
          description={errorMessage(error)}
          action={
            <Button size="sm" onClick={refetch}>
              Повторить
            </Button>
          }
        />
      ) : pools.length === 0 ? (
        <EmptyState
          icon="layers"
          title="Пулов пока нет"
          description="Пул объединяет линии под рынок или клиента"
          action={
            <Button variant="accent" size="sm" onClick={() => setCreateOpen(true)}>
              Завести пул
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2 2xl:grid-cols-3">
          {pools.map((pool) => (
            <PoolCard key={pool.id} pool={pool} />
          ))}
        </div>
      )}

      <CreatePoolSlideOver open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  )
}

function PoolCard({ pool }: { pool: TelephonyPoolView }) {
  return (
    <CardLink
      to="/telephony/pools/$poolId"
      params={{ poolId: pool.id }}
      className="flex flex-col gap-4 p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-control bg-raise text-muted">
            <Icon name="layers" size={20} />
          </span>
          <div className="min-w-0">
            <p className="truncate text-body-md font-medium">{pool.name}</p>
            <p className="truncate text-caption text-faint">{pool.id}</p>
          </div>
        </div>
        <StatusBadge {...poolStatus(pool.status)} />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Chip tone="tag">#{pool.market.toUpperCase()}</Chip>
        <Chip>{marketLabel(pool.market)}</Chip>
        <Chip tone={pool.owner_type === 'client' ? 'accent' : 'neutral'}>
          {ownerTypeLabel(pool.owner_type)}
          {pool.owner_account_id ? ` · ${pool.owner_account_id}` : ''}
        </Chip>
      </div>
    </CardLink>
  )
}

function CreatePoolSlideOver({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    id: '',
    name: '',
    market: 'ru',
    ownerType: 'platform',
    ownerAccountId: '',
  })
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }))

  const create = useMutation({
    ...createTelephonyPoolMutation(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listTelephonyPoolsQueryKey() })
      close()
    },
  })

  const close = () => {
    create.reset()
    setForm({
      id: '',
      name: '',
      market: 'ru',
      ownerType: 'platform',
      ownerAccountId: '',
    })
    onClose()
  }

  return (
    <SlideOver
      open={open}
      onClose={close}
      title="Новый пул"
      footer={
        <>
          <Button onClick={close}>Отмена</Button>
          <Button
            variant="primary"
            pending={create.isPending}
            disabled={
              !form.id.trim() ||
              !form.name.trim() ||
              (form.ownerType === 'client' && !form.ownerAccountId.trim())
            }
            onClick={() =>
              create.mutate({
                body: {
                  id: form.id.trim(),
                  name: form.name.trim(),
                  market: form.market as 'ru' | 'sa' | 'eu',
                  owner_type: form.ownerType as 'platform' | 'client',
                  ...(form.ownerType === 'client'
                    ? { owner_account_id: form.ownerAccountId.trim() }
                    : {}),
                },
              })
            }
          >
            Создать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <TextField
          label="ID"
          required
          placeholder="pool-ru-main"
          value={form.id}
          onChange={(e) => set({ id: e.target.value })}
        />
        <TextField
          label="Название"
          required
          placeholder="RU основной"
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
        />
        <SelectField
          label="Рынок"
          value={form.market}
          onChange={(e) => set({ market: e.target.value })}
        >
          {Object.entries(marketLabels).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Владелец"
          value={form.ownerType}
          onChange={(e) => set({ ownerType: e.target.value })}
        >
          <option value="platform">Платформа</option>
          <option value="client">Клиент (BYOC)</option>
        </SelectField>
        {form.ownerType === 'client' && (
          <TextField
            label="Аккаунт клиента"
            required
            placeholder="client-204"
            value={form.ownerAccountId}
            onChange={(e) => set({ ownerAccountId: e.target.value })}
          />
        )}
        <p className="rounded-control bg-raise px-4 py-2.5 text-caption text-muted">
          Ёмкость пула стоячей не бывает: линии превращаются в слоты только под заявку на ёмкость.
          Сколько каналов пул продаёт — на доске ёмкости.
        </p>
        {create.isError && (
          <p className="rounded-control bg-danger/10 px-4 py-2.5 text-body-sm text-danger">
            {errorMessage(create.error)}
          </p>
        )}
      </div>
    </SlideOver>
  )
}
